/**
 * @file   src/Pages/GiftCardManagement.tsx
 * @brief  Gift Card Management page.
 */
import {
  React,
  useEffect,
  useOutletContext,
  useRef,
  useState,
  Container,
  Table,
  Badge,
  Button,
  Modal,
  Pagination,
} from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import AddIcon from '../assets/images/icons/addicon';
import EditIcon from '../assets/images/icons/editicon';
// import DeleteIcon from '../assets/images/icons/deleteicon';
import CWInput from '../components/CWInput';
import CWSelect from '../components/CWSelect';
import CWMultiSelect from '../components/CWMultiSelect';
import CWSwitch from '../components/CWSwitch';
import SmallLoader from '../components/SmallLoader';
import axios from '../config/axiosConfig';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import { GiftCardApi, LocationApi } from '../utils/apiUrls';
import { GIFT_CARD_AMOUNT_MAX_LENGTH, GIFT_CARD_NAME_MAX_LENGTH } from '../utils/constants';
import { isEnabledFlag } from '../utils/locationFleetSettings';
import { MessageToaster } from '../utils/toastUtil';

type GiftCardStatus = 'active' | 'inactive';

interface GiftCardItem {
  publicId?: string;
  giftCardProductId?: number;
  name: string;
  value: string;
  purchasePrice: string;
  status: GiftCardStatus;
  locationId?: number;
  locationIds?: number[];
  locationName?: string;
  redeemAtAllLocations?: boolean;
}

const statusOptions = [
  { label: 'Yes', value: 'active' },
  { label: 'No', value: 'inactive' },
];

const emptyForm = {
  name: '',
  value: '',
  purchasePrice: '',
  status: '' as '' | GiftCardStatus,
  locationIds: [] as number[],
  redeemAtAllLocations: false,
};

const emptyErrors = { name: '', value: '', purchasePrice: '', status: '', locationId: '' };

const limitAmountDecimals = (value: string): string | null => {
  if (value === '') {
    return '';
  }
  if (!/^\d*\.?\d*$/.test(value)) {
    return null;
  }
  const [whole, fraction] = value.split('.');
  if (fraction === undefined) {
    return whole;
  }
  return `${whole}.${fraction.slice(0, 2)}`;
};

const limitAmountInput = (value: string): string | null => {
  const limited = limitAmountDecimals(value);
  if (limited === null) {
    return null;
  }
  return limited.slice(0, GIFT_CARD_AMOUNT_MAX_LENGTH);
};

const getApiErrorMessage = (error: any, fallback: string) =>
  error?.data?.message || error?.response?.data?.message || error?.message || fallback;

const parseLocationIds = (value: unknown): number[] => {
  const rawValues = Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value];
  return rawValues
    .map((entry) => {
      if (entry && typeof entry === 'object') {
        const record = entry as { locationId?: unknown; id?: unknown };
        return Number(record.locationId ?? record.id);
      }
      return Number(entry);
    })
    .filter((id) => Number.isFinite(id) && id > 0);
};

const mapGiftCard = (item: any): GiftCardItem => {
  const locationIds = parseLocationIds(
    Array.isArray(item?.locationIds) && item.locationIds.length ? item.locationIds : item?.locationId
  );
  const publicId = String(item?.publicId ?? item?.giftCardProductPublicId ?? '').trim();
  const giftCardProductId = Number(item?.giftCardProductId ?? 0);
  return {
    publicId: publicId || undefined,
    giftCardProductId: Number.isFinite(giftCardProductId) && giftCardProductId > 0 ? giftCardProductId : undefined,
    name: String(item?.name ?? ''),
    value: String(item?.faceValue ?? ''),
    purchasePrice: String(item?.purchasePrice ?? ''),
    status: isEnabledFlag(item?.isActive) ? 'active' : 'inactive',
    locationId: locationIds[0],
    locationIds,
    locationName: item?.locationName ?? '',
    redeemAtAllLocations: isEnabledFlag(item?.redeemAtAllLocations),
  };
};

const getProductIdentity = (item: Pick<GiftCardItem, 'publicId' | 'giftCardProductId'>) => {
  if (item.publicId) {
    return { giftCardProductPublicId: item.publicId };
  }
  if (item.giftCardProductId) {
    return { giftCardProductId: item.giftCardProductId };
  }
  return null;
};

const extractGiftCardRows = (body: any): any[] => {
  if (Array.isArray(body)) {
    return body;
  }
  if (Array.isArray(body?.list)) {
    return body.list;
  }
  if (Array.isArray(body?.giftCards)) {
    return body.giftCards;
  }
  if (Array.isArray(body?.products)) {
    return body.products;
  }
  return [];
};

const GiftCardManagement = () => {
  const { updateData } = useOutletContext<AppContextType>();
  const toaster = new MessageToaster();
  const [giftCards, setGiftCards] = useState<GiftCardItem[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<Option[]>([]);
  const [appliedLocationIds, setAppliedLocationIds] = useState<number[]>([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCard, setEditingCard] = useState<Pick<GiftCardItem, 'publicId' | 'giftCardProductId'> | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyErrors);
  const [giftCardToDelete, setGiftCardToDelete] = useState<GiftCardItem | null>(null);
  const [isGiftCardEnabled, setIsGiftCardEnabled] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const listRequestId = useRef(0);

  const getCompanyLocations = async () => {
    try {
      const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
      if (data?.error === 0 && Array.isArray(data?.data)) {
        const locationList: LocationItem[] = data.data;
        setLocations(
          locationList.map((item) => ({
            label: item.locationName,
            value: item.locationId,
          }))
        );
      } else {
        // toaster.toastError(data?.message || 'Failed to load locations.');
      }
    } catch (error: any) {
      // toaster.toastError(getApiErrorMessage(error, 'Failed to load locations.'));
    }
  };

  const getFilterLocationIds = () => {
    const ids = selectedLocations
      .map((location) => Number(location.value))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (locations.length > 0 && ids.length === locations.length) {
      return [];
    }
    return ids;
  };

  const getGiftCards = async (locationIds: number[], pageNo = 1) => {
    const requestId = listRequestId.current + 1;
    listRequestId.current = requestId;
    try {
      setLoading(true);
      const query = new URLSearchParams({ pageNo: String(pageNo) });
      if (locationIds.length === 1) {
        query.set('locationId', String(locationIds[0]));
      }
      const { data } = await axios.get(`${GiftCardApi.LIST}?${query.toString()}`);
      if (requestId !== listRequestId.current) {
        return;
      }
      const isSuccess = data?.error === 0 || data?.statusCode === 1000;
      if (!isSuccess) {
        setGiftCards([]);
        setIsGiftCardEnabled(null);
        setTotalPages(1);
        // toaster.toastError(data?.message || 'Failed to load gift cards.');
        return;
      }
      const body = data?.data ?? data?.responseBody;
      const selectedIds = new Set(locationIds);
      const mergedRows = new Map<string, GiftCardItem>();
      extractGiftCardRows(body).map(mapGiftCard).forEach((item, index) => {
        const key = item.publicId || String(item.giftCardProductId || `row-${index}`);
        const existing = mergedRows.get(key);
        if (!existing) {
          mergedRows.set(key, item);
          return;
        }
        const combinedLocationIds = Array.from(new Set([...(existing.locationIds || []), ...(item.locationIds || [])]));
        mergedRows.set(key, {
          ...existing,
          locationId: combinedLocationIds[0],
          locationIds: combinedLocationIds,
        });
      });
      const giftCardRows = Array.from(mergedRows.values()).filter((item) => {
        if (locationIds.length <= 1) {
          return true;
        }
        const itemLocationIds = item.locationIds?.length ? item.locationIds : item.locationId ? [item.locationId] : [];
        return itemLocationIds.some((id) => selectedIds.has(id));
      });
      const hasFeatureFlag = locationIds.length === 1
        && body
        && !Array.isArray(body)
        && Object.prototype.hasOwnProperty.call(body, 'isGiftCardEnabled');
      const totalCount = Number(body?.totalCount ?? body?.TotalRows ?? giftCardRows.length);
      const limit = Number(body?.limit) || giftCardRows.length || 1;
      setGiftCards(giftCardRows);
      setIsGiftCardEnabled(hasFeatureFlag ? isEnabledFlag(body.isGiftCardEnabled) : null);
      setTotalPages(Math.ceil(totalCount / limit) || 1);
    } catch (error: any) {
      if (requestId !== listRequestId.current) {
        return;
      }
      setGiftCards([]);
      setIsGiftCardEnabled(null);
      setTotalPages(1);
      // toaster.toastError(getApiErrorMessage(error, 'Failed to load gift cards.'));
    } finally {
      if (requestId === listRequestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    updateData({
      heading: 'Gift Card Management',
      url: '',
      showBackButton: false,
      showMainMenu: true,
      activeLink: 'gift-card-management',
    });
    getCompanyLocations();
    getGiftCards([], 1);
  }, []);

  const applyLocationFilter = () => {
    const locationIds = getFilterLocationIds();
    setAppliedLocationIds(locationIds);
    setCurrentPage(1);
    getGiftCards(locationIds, 1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) {
      return;
    }
    setCurrentPage(newPage);
    getGiftCards(appliedLocationIds, newPage);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
    let endPage = startPage + totalPageNumbersToShow - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextValue = name === 'value' || name === 'purchasePrice'
      ? limitAmountInput(value)
      : name === 'name'
        ? value.slice(0, GIFT_CARD_NAME_MAX_LENGTH)
        : value;
    if (nextValue === null) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (name === 'name' || name === 'value' || name === 'purchasePrice') {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const openAddModal = () => {
    setEditingCard(null);
    setFormData(emptyForm);
    setErrors(emptyErrors);
    setShowFormModal(true);
  };

  const openEditModal = async (item: GiftCardItem) => {
    const productKey = item.publicId || item.giftCardProductId;
    if (!productKey) {
      toaster.toastError('This gift card is missing an id.');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get(`${GiftCardApi.DETAILS}${productKey}`);
      const isSuccess = data?.error === 0 || data?.statusCode === 1000;
      const body = data?.data ?? data?.responseBody;
      const detail = body?.giftCard ?? body?.product ?? body;
      if (!isSuccess || !detail || Array.isArray(detail)) {
        toaster.toastError(data?.message || 'Failed to load gift card details.');
        return;
      }

      const mapped = mapGiftCard(detail);
      const locationIds = mapped.locationIds?.length
        ? mapped.locationIds
        : item.locationIds?.length
          ? item.locationIds
          : parseLocationIds(detail.locationId);
      setEditingCard({
        publicId: mapped.publicId || item.publicId,
        giftCardProductId: mapped.giftCardProductId || item.giftCardProductId,
      });
      setFormData({
        name: (mapped.name || item.name).slice(0, GIFT_CARD_NAME_MAX_LENGTH),
        value: limitAmountInput(mapped.value || item.value) ?? (mapped.value || item.value),
        purchasePrice: limitAmountInput(mapped.purchasePrice || item.purchasePrice) ?? (mapped.purchasePrice || item.purchasePrice),
        status: mapped.status || item.status,
        locationIds,
        redeemAtAllLocations: isRedeemAtAllLocationsEnabled(locationIds)
          ? (Object.prototype.hasOwnProperty.call(detail, 'redeemAtAllLocations')
            ? isEnabledFlag(detail.redeemAtAllLocations)
            : Boolean(item.redeemAtAllLocations))
          : true,
      });
      setErrors(emptyErrors);
      setShowFormModal(true);
    } catch (error: any) {
      toaster.toastError(getApiErrorMessage(error, 'Failed to load gift card details.'));
    } finally {
      setLoading(false);
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingCard(null);
  };

  const getAmountError = (value: string, emptyMessage: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return emptyMessage;
    }
    const normalized = trimmed.startsWith('.') ? `0${trimmed}` : trimmed;
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      return emptyMessage;
    }
    if (Number(normalized) < 1) {
      return 'Minimum 1 dollar.';
    }
    return '';
  };

  const isRedeemAtAllLocationsEnabled = (selectedLocationIds: number[]) => {
    const companyLocationIds = locations
      .map((option) => Number(option.value))
      .filter((id) => id > 0);
    const allLocationsSelected = companyLocationIds.length > 0
      && companyLocationIds.every((id) => selectedLocationIds.includes(id));
    return companyLocationIds.length > 1 && !allLocationsSelected;
  };

  const saveGiftCard = async () => {
    if (loading) {
      return;
    }

    const locationIds = formData.locationIds.filter((id) => id > 0);
    const nextErrors = {
      locationId: locationIds.length ? '' : 'Select location.',
      name: formData.name.trim() ? '' : 'Enter gift card name.',
      value: getAmountError(formData.value, 'Enter gift card value.'),
      purchasePrice: getAmountError(formData.purchasePrice, 'Enter purchase price.'),
      status: formData.status ? '' : 'Select purchase status.',
    };
    setErrors(nextErrors);
    if (
      nextErrors.locationId ||
      nextErrors.name ||
      nextErrors.value ||
      nextErrors.purchasePrice ||
      nextErrors.status ||
      !formData.status
    ) {
      return;
    }

    const isEdit = Boolean(editingCard);
    const productIdentity = isEdit && editingCard ? getProductIdentity(editingCard) : null;
    if (isEdit && !productIdentity) {
      toaster.toastError('This gift card is missing an id.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...(productIdentity ?? {}),
        locationIds,
        redeemAtAllLocations: !isRedeemAtAllLocationsEnabled(locationIds) || formData.redeemAtAllLocations,
        name: formData.name.trim(),
        faceValue: Number(formData.value.trim()),
        purchasePrice: Number(formData.purchasePrice.trim()),
        isActive: formData.status === 'active',
      };
      const { data } = await axios.post(isEdit ? GiftCardApi.EDIT : GiftCardApi.CREATE, payload);
      const isSuccess = data?.error === 0 || data?.statusCode === 1000;
      if (isSuccess) {
        toaster.toastSuccess(data?.message || (isEdit ? 'Gift card updated successfully.' : 'Gift card created successfully.'));
        closeFormModal();
        await getGiftCards(appliedLocationIds, currentPage);
      } else {
        toaster.toastError(data?.message || (isEdit ? 'Failed to update gift card.' : 'Failed to create gift card.'));
      }
    } catch (error: any) {
      toaster.toastError(getApiErrorMessage(error, isEdit ? 'Failed to update gift card.' : 'Failed to create gift card.'));
    } finally {
      setLoading(false);
    }
  };

  const closeDeleteModal = () => {
    if (loading) {
      return;
    }
    setGiftCardToDelete(null);
  };

  const confirmDelete = async () => {
    if (!giftCardToDelete || loading) {
      return;
    }
    if (!giftCardToDelete.locationId) {
      toaster.toastError('Location is required to delete this gift card.');
      return;
    }
    const productIdentity = getProductIdentity(giftCardToDelete);
    if (!productIdentity) {
      toaster.toastError('This gift card is missing an id.');
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(GiftCardApi.DELETE, {
        ...productIdentity,
        locationId: giftCardToDelete.locationId,
      });
      const isSuccess = data?.error === 0 || data?.statusCode === 1000;
      if (isSuccess) {
        toaster.toastSuccess(data?.message || 'Gift card deleted successfully.');
        setGiftCardToDelete(null);
        await getGiftCards(appliedLocationIds, currentPage);
      } else {
        toaster.toastError(data?.message || 'Failed to delete gift card.');
      }
    } catch (error: any) {
      toaster.toastError(getApiErrorMessage(error, 'Failed to delete gift card.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-carwash-container">
      <Container>
        <div className="manage-header-block d-flex justify-content-between align-items-end">
          <div className="d-flex align-items-end">
            <div style={{ width: 420 }}>
              <CWMultiSelect
                id="giftCardListLocation"
                name="filterLocationId"
                options={locations}
                label="Location"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="All Locations"
                hasSelectAll
                labelAllSelected="All Locations Selected."
                value={selectedLocations}
                onChange={(selectedOptions) => setSelectedLocations(selectedOptions || [])}
              />
            </div>
            <Button
              title="Search"
              type="button"
              variant="secondary"
              className="secondary-btn ms-3"
              onClick={applyLocationFilter}
              disabled={loading}
            >
              Search
            </Button>
          </div>
          <Button
              title="Add Gift Card"
            type="button"
            variant="primary"
            className="primary-btn ms-auto d-flex align-items-center"
            onClick={openAddModal}
          >
            Add Gift Card
            <AddIcon />
          </Button>
        </div>
        {appliedLocationIds.length === 1 && isGiftCardEnabled === false ? (
          <p className="promo-note">Gift cards are turned off for this location.</p>
        ) : null}
        <div className="cw-table-wrap">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th className="nowrap">Gift Card Name</th>
                <th className="nowrap">Locations</th>
                <th className="nowrap">Gift Card Value ($)</th>
                <th className="nowrap">Purchase Price ($)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {giftCards.length ? (
                giftCards.map((item, index) => (
                  <tr key={item.publicId || String(item.giftCardProductId || index)}>
                    <td>{item.name}</td>
                    <td>{
                      (item.locationIds?.length ? item.locationIds : item.locationId ? [item.locationId] : [])
                        .map((id) => locations.find((option) => Number(option.value) === id)?.label)
                        .filter(Boolean)
                        .join(', ') || item.locationName || ''
                    }</td>
                    <td>{item.value}</td>
                    <td>{item.purchasePrice}</td>
                    <td>
                      <Badge className={item.status === 'inactive' ? 'inactive' : 'active'}>
                        {item.status === 'inactive' ? 'Inactive' : 'Active'}
                      </Badge>
                    </td>
                    <td className="nowrap">
                      <div className="active-group d-flex align-items-center">
                        <Button
                          title="Edit"
                          variant="light"
                          className="light-btn action-btn"
                          onClick={() => openEditModal(item)}
                        >
                          <EditIcon />
                        </Button>
                        {/* Temporarily hidden
                        <Button
                          title="Delete"
                          variant="light"
                          className="light-btn action-btn"
                          onClick={() => setGiftCardToDelete(item)}
                        >
                          <DeleteIcon />
                        </Button>
                        */}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ textAlign: 'center' }} colSpan={6}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {giftCards.length || totalPages > 1 ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First disabled={currentPage === 1} onClick={() => handlePageChange(1)} />
                <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                <Pagination.Last disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)} />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      <Modal
        show={showFormModal}
        onHide={closeFormModal}
        centered
        className="add-modal"
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <Modal.Body>
          <div>
            <h5 className="f-bold">{editingCard ? 'Edit Gift Card' : 'Add Gift Card'}</h5>
            <CWMultiSelect
              id="giftCardLocation"
              name="locationId"
              options={locations}
              value={locations.filter((option) => formData.locationIds.includes(Number(option.value)))}
              label="Purchase Location"
              dropdownGap={0}
              dropdownPosition="auto"
              placeholder="Select Location"
              hasSelectAll
              labelAllSelected="All Locations Selected."
              isMandatory
              errorMessage={errors.locationId}
              onChange={(selectedOptions) => {
                const nextLocationIds = (selectedOptions || [])
                  .map((option: Option) => Number(option.value))
                  .filter((id: number) => id > 0);
                setFormData((prev) => ({
                  ...prev,
                  locationIds: nextLocationIds,
                  redeemAtAllLocations: isRedeemAtAllLocationsEnabled(nextLocationIds) ? prev.redeemAtAllLocations : true,
                }));
                setErrors((prev) => ({ ...prev, locationId: '' }));
              }}
            />
            <CWInput
              id="giftCardName"
              name="name"
              type="text"
              label="Gift Card Name"
              value={formData.name}
              maxLength={GIFT_CARD_NAME_MAX_LENGTH}
              onChange={handleChange}
              errorMessage={errors.name}
              autoFocus
            />
            <div className="gift-value-field">
              <CWInput
                id="giftCardValue"
                name="value"
                type="text"
                label="Gift Card Value ($)"
                value={formData.value}
                maxLength={GIFT_CARD_AMOUNT_MAX_LENGTH}
                onChange={handleChange}
                errorMessage={errors.value}
              />
              <p className="promo-note">Amount for which customer can use the card</p>
            </div>
            <div className="gift-value-field">
              <CWInput
                id="purchasePrice"
                name="purchasePrice"
                type="text"
                label="Purchase Price ($)"
                value={formData.purchasePrice}
                maxLength={GIFT_CARD_AMOUNT_MAX_LENGTH}
                onChange={handleChange}
                errorMessage={errors.purchasePrice}
              />
              <p className="promo-note">Amount customer need to pay</p>
            </div>
            <CWSelect
              id="giftCardStatus"
              name="status"
              options={statusOptions}
              values={statusOptions.filter((option) => option.value === formData.status)}
              label="Purchase Status"
              dropdownGap={0}
              dropdownPosition="auto"
              placeholder="Select"
              multi={false}
              searchable={false}
              errorMessage={errors.status}
              onChange={(selectedOptions) => {
                const value = selectedOptions?.[0]?.value ?? '';
                setFormData((prev) => ({ ...prev, status: value }));
                setErrors((prev) => ({ ...prev, status: '' }));
              }}
            />
            <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
              <p className="label m-0">Allow using this Gift Card on all Locations</p>
              <CWSwitch
                id="allowGiftCardOnAllLocations"
                name="redeemAtAllLocations"
                checked={!isRedeemAtAllLocationsEnabled(formData.locationIds) || formData.redeemAtAllLocations}
                disabled={!isRedeemAtAllLocationsEnabled(formData.locationIds)}
                onChange={() => {
                  if (!isRedeemAtAllLocationsEnabled(formData.locationIds)) {
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    redeemAtAllLocations: !prev.redeemAtAllLocations,
                  }));
                }}
              />
            </div>
            <div className="btn-sec d-grid">
              <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={closeFormModal} disabled={loading}>
                Cancel
              </Button>
              <Button title="Save" variant="primary" className="primary-btn m-0" onClick={saveGiftCard} disabled={loading}>
                Save
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(giftCardToDelete)}
        onHide={closeDeleteModal}
        centered
        className="add-modal"
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <Modal.Body className="text-center">
          <h5 className="f-bold text-center">
            Do you want to delete the gift card {giftCardToDelete?.name}?
          </h5>
          <div className="btn-sec d-grid">
            <Button title="Not Now" variant="light" className="secondary-btn" onClick={closeDeleteModal} disabled={loading}>
              Not Now
            </Button>
            <Button title="Yes" variant="primary" className="primary-btn btn-main m-0" onClick={confirmDelete} disabled={loading}>
              Yes
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      {loading && <SmallLoader />}
    </div>
  );
};

export default GiftCardManagement;
